"use strict";
var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (b.hasOwnProperty(p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
Object.defineProperty(exports, "__esModule", { value: true });
var React = require("react");
var RoutableComponentsManager_1 = require("../RoutableComponentsManager");
var BrowserHistory_1 = require("../BrowserHistory");
var utils_1 = require("./utils");
var RoutableContainer_config_1 = require("./RoutableContainer.config");
var ScrollListeners_1 = require("../ScrollListeners");
var springer_1 = require("./utils/springer");
var RoutableContainer = /** @class */ (function (_super) {
    __extends(RoutableContainer, _super);
    function RoutableContainer() {
        var _this = _super !== null && _super.apply(this, arguments) || this;
        _this.ref = (_this.props.container || document);
        _this.manager = RoutableComponentsManager_1.RoutableComponentsManager.getInstance();
        /** If the scroll listener is triggered while throttling, make sure to run it again after the timeout. */
        _this.triggeredWhileThrottling = false;
        /** Whether we are currently pushing to the history ourselves */
        _this.internallyPushingToHistory = false;
        /** The handle for the currently running animation frame */
        _this.animationFrameHandle = 0;
        _this.cancelAnimation = function () {
            cancelAnimationFrame(_this.animationFrameHandle);
        };
        _this.onScroll = function (event) {
            if (!_this.scrollThrottle) {
                _this.clearInitialCheckInterval();
                _this.processScroll(event.currentTarget);
                _this.scrollThrottle = window.setTimeout(function () {
                    _this.scrollThrottle = undefined;
                    if (_this.triggeredWhileThrottling) {
                        _this.triggeredWhileThrottling = false;
                        _this.processScroll(event.currentTarget);
                    }
                }, RoutableContainer_config_1.RoutableContainerConfig.scrollThrottle);
            }
            else {
                _this.triggeredWhileThrottling = true;
            }
        };
        _this.processScroll = function (e) {
            if (!e) {
                return;
            }
            var scrollTop = e.scrollTop || document.documentElement.scrollTop || window.scrollY;
            var active = utils_1.getTopComponent(_this.manager.components, scrollTop);
            if (active && active.route !== utils_1.cleanSlashes(BrowserHistory_1.history.location.pathname)) {
                if (active.onDisplay) {
                    active.onDisplay(active.route);
                }
                // If the user is just scrolling, only replace the route – we don't want to add new entries.
                _this.internallyPushingToHistory = true;
                BrowserHistory_1.history.replace(active.route + (active.appendSlash ? '/' : ''));
            }
        };
        return _this;
    }
    RoutableContainer.prototype.componentDidMount = function () {
        this.scrollListener = ScrollListeners_1.scrollListeners.create(this.onScroll, this.ref, this.cancelAnimation);
        this.scrollListener.startListeningToScrollOnUserInteraction();
        this.handleInitialRoute();
        this.startListeningToHistory();
    };
    RoutableContainer.prototype.componentWillUnmount = function () {
        ScrollListeners_1.scrollListeners.destroy(this.scrollListener);
        if (this.stopListeningToHistory) {
            this.stopListeningToHistory();
        }
        this.internallyPushingToHistory = false;
        this.scrollThrottle = undefined;
    };
    RoutableContainer.prototype.render = function () {
        return this.props.children;
    };
    RoutableContainer.prototype.startListeningToHistory = function () {
        var _this = this;
        if (this.stopListeningToHistory) {
            this.stopListeningToHistory();
        }
        this.stopListeningToHistory = BrowserHistory_1.history.listen(function (_a) {
            var pathname = _a.pathname;
            if (_this.initialCheckInterval) {
                _this.clearInitialCheckInterval();
            }
            if (_this.internallyPushingToHistory) {
                _this.internallyPushingToHistory = false;
                return;
            }
            var component = _this.manager.getComponentByRoute(pathname);
            if (component) {
                if (component.onDisplay) {
                    component.onDisplay(component.route);
                }
                if (component.ref.current) {
                    _this.scrollTo(component.ref.current);
                }
            }
        });
    };
    /**
     * Scroll to a passed element, and call a callback when done.
     * @param element The element to scroll to.
     * @param callback The callback to call.
     */
    RoutableContainer.prototype.scrollTo = function (element) {
        var _this = this;
        if (!element) {
            return;
        }
        if (this.scrollListener) {
            this.scrollListener.startListeningToScrollOnUserInteraction();
        }
        // We need to manually scroll to make it crossplatform.
        var doc = document.documentElement;
        var start = doc.scrollTop;
        var max = doc.scrollHeight - window.innerHeight;
        var end = Math.min(max, start + Math.round(element.getBoundingClientRect().top));
        var totalDelta = end - start;
        // Make the duration dynamic based on the amount needed to scroll. This ratio felt right.
        var duration = Math.abs(totalDelta) / 5 + 1000;
        var spring = springer_1.default(0, 0);
        var startTime;
        var step = function (t) {
            if (!startTime) {
                if (startTime == null) {
                    startTime = 0;
                }
                else {
                    startTime = t;
                }
                requestAnimationFrame(step);
                return;
            }
            var deltaTime = t - startTime;
            // We're not at the duration yet and we are not yet to our destination
            if (deltaTime < duration || Math.abs(doc.scrollTop - end) > 1) {
                _this.animationFrameHandle = requestAnimationFrame(step);
                var proportion = Math.min(deltaTime / duration, 1);
                var deltaPercent = spring(proportion);
                var delta = deltaPercent * totalDelta;
                doc.scrollTop = start + Math.round(delta);
            }
            else {
                doc.scrollTop = end;
            }
        };
        this.cancelAnimation();
        // Only animate anew if needed
        if (Math.abs(totalDelta) > 0) {
            this.animationFrameHandle = requestAnimationFrame(step);
        }
    };
    RoutableContainer.prototype.clearInitialCheckInterval = function () {
        if (this.initialCheckInterval) {
            clearInterval(this.initialCheckInterval);
            this.initialCheckInterval = undefined;
        }
    };
    RoutableContainer.prototype.handleInitialRoute = function () {
        var _this = this;
        var data;
        if (this.props.appendSlashInInitialRoute && !BrowserHistory_1.history.location.pathname.endsWith('/')) {
            BrowserHistory_1.history.location.pathname += '/';
        }
        var startedCheckingAt = performance.now();
        this.initialCheckInterval = window.setInterval(function () {
            var stopping = performance.now() > startedCheckingAt + 2000;
            if (stopping) {
                _this.clearInitialCheckInterval();
            }
            var route = BrowserHistory_1.history.location.pathname;
            if (!data) {
                data = _this.manager.getComponentByRoute(route);
            }
            // Let's wait until we find a route and its component is registered.
            if (data && data.ref.current) {
                if (data.onDisplay) {
                    data.onDisplay(route);
                }
                // Scroll to it if the browser isn't already scrolled.
                if (window.scrollY === 0) {
                    _this.scrollTo(data.ref.current);
                }
                _this.clearInitialCheckInterval();
            }
            if (stopping && !(data && data.ref.current)) {
                var ref = React.createRef();
                ref.current = document.getElementById('root') || document.documentElement;
                _this.manager.registerComponent(route, ref, undefined, _this.props.appendSlashInInitialRoute);
            }
        }, 200);
    };
    return RoutableContainer;
}(React.Component));
exports.default = RoutableContainer;
//# sourceMappingURL=RoutableContainer.js.map


//////////////////
// WEBPACK FOOTER
// ./node_modules/@idp/routables/RoutableContainer/RoutableContainer.js
// module id = 1007
// module chunks = 8